<template>
  <ValidationObserver ref="formObserver" v-slot="{ handleSubmit }">
    <form @submit.prevent="handleSubmit(onSubmit)">
      <div class="rounded-lg bg-smoke-100 py-4 px-2 lg:px-4 md:py-7.5 md:px-12 xl:px-56">
        <div class="flex border-b border-dark-b text-xl md:text-2xl font-semibold">
          Product Details
        </div>
        <p class="lg:py-14 py-9 text-base font-normal lg:text-lg">
          Please input only <u> one</u> Product Category per project. If you want more
          than one category, you’ll need to fill this form out again as a
          separate project.
        </p>
        <div class="flex flex-wrap w-full">
          <div class="lg:pb-0 pb-3 lg:pr-5 w-full lg:w-1/2">
            <div class="lg:pb-1 font-semibold text-lg lg:text-xl">
              Product Category<span class="text-red-600">*</span>
            </div>
            <ValidationProvider v-slot="{ errors }" rules="required">
              <select
                name="productCategory"
                class="form-input"
                v-model="formData.product_category_id"
              >
                <option value disabled class="opacity-50">Select Product Category</option>
                <option
                  v-for="(cat, index) in getFreelancerCategories"
                  :key="`categories-${index}`"
                  :value="cat.category_id_token"
                >
                  {{ cat.category_name }}
                </option>
              </select>
              <div class="input-error --end">
                <span v-if="errors[0]">This field is required</span>
              </div>
            </ValidationProvider>
          </div>
          <div class="lg:pb-0 pb-3 lg:pl-5 w-full lg:w-1/2">
            <div class="lg:pb-1 font-semibold text-lg lg:text-xl">
              Product Sub Category<span class="text-red-600">*</span>
            </div>
            <ValidationProvider v-slot="{ errors }" ref="subCategoriesField" rules="required">
              <select
                name="productSubCategory"
                class="form-input"
                v-model="formData.product_sub_category_id"
              >
              <option value disabled>Select Product Sub-Category</option>
                <template v-if="selectedCategory">
                  <option
                    v-for="(sub, index) in subCategories"
                    :key="`sub-category-${index}`"
                    :value="sub.sub_category_id"
                  >
                    {{ sub.sub_category_name }}
                  </option>
                </template>
                <template v-else>
                  <option value="1"></option>
                </template>
              </select>
              <div class="input-error --end">
                <span v-if="errors[0]">This field is required</span>
              </div>
            </ValidationProvider>
          </div>
        </div>
        <div class="pb-4 lg:pb-0">
          <div class="lg:pb-1 lg:flex items-center">
            <div class="font-semibold text-lg lg:text-xl">
              Product Name & Description<span class="text-red-600">*&nbsp; </span>
            </div>
            <div class="lg:ml-2">(Max 1000 Characters)</div>
          </div>
          <ValidationProvider v-slot="{ errors }" rules="max:1000|required">
            <textarea
              class="border-2 rounded-lg p-3 w-full lg:h-32 h-48 text-lg"
              name="The Product name & description"
              placeholder="e.g. 3 Ply Face Masks, Level 1, 100 pieces per box"
              v-model="formData.product_description"
            />
            <div class="pl-3 text-sm text-red-500 h-10 sm:h-5 --end">
              {{ errors[0] ? checkValidation(errors[0],'The Product name & description') : ''}}
            </div>
          </ValidationProvider>
        </div>
        <div class="flex flex-wrap w-full">
          <div class="lg:pb-0 pb-3 lg:pr-5 w-full lg:w-1/2">
            <div class="lg:pb-1 font-semibold text-lg lg:text-xl relative">
              Quantity<span class="text-red-600">*</span>
               <span class="md:relative" 
                  @mouseenter="shownTooltip = 'quantity'"
                  @mouseleave="shownTooltip = null"
               >
                  ⓘ
                  <div v-if="shownTooltip === 'quantity'" class="absolute z-20 px-3 small-tooltip">
                    <div class="p-2 pr-8 w-content text-xs font-normal border rounded-md bg-gray-b shadow-md">
                      e.g. 1,000 units or 1,000 sets 
                    </div>
                  </div>
               </span>
            </div>
            <ValidationProvider v-slot="{ errors }" rules="required">
              <input
                name="quantity"
                class="form-input p-2"
                placeholder="Type Quantity Here..."
                v-model="formData.product_quantity"
              />
              <div class="input-error --end">
                <span v-if="errors[0]">This field is required</span>
              </div>
            </ValidationProvider>
          </div>
          <div class="lg:pb-0 pb-1 lg:pl-5 w-full lg:w-1/2">
            <div class="lg:pb-1 font-semibold text-lg lg:text-xl">
              Shipping Terms<span class="text-red-600">*</span>
            </div>
            <ValidationProvider v-slot="{ errors }" rules="required">
              <select
                name="productIncoTerms"
                class="form-input"
                v-model="formData.product_shipping_terms"
              >
                <option value="FOB">FOB</option>
                <option value="EXW">EXW</option>
                <option value="CIF">CIF</option>
                <option value="DDP">DDP</option>
                <option value="DDU">DDU</option>
              </select>
              <div class="input-error --end">
                <span v-if="errors[0]">This field is required</span>
              </div>
            </ValidationProvider>
          </div>
        </div>
        <div class="flex flex-wrap w-full">
          <div class="lg:pb-0 pb-3 lg:pr-5 w-full lg:w-1/2">
            <div class="lg:pb-1 lg:flex lg:inline relative items-center leading-6 lg:leading-normal">
                <span class="font-semibold text-lg lg:text-xl ">
                  Packing Details<span class="text-red-600">*&nbsp; </span>
                </span>
                <span class="md:relative mx-1 font-semibold text-lg"
                  @mouseenter="shownTooltip = 'packing'"
                  @mouseleave="shownTooltip = null"
                >
                  ⓘ
                  <div v-if="shownTooltip==='packing'" class="absolute z-20 px-3 large-tooltip">
                    <div class="p-2 w-content text-xs font-normal border rounded-lg bg-gray-b leading-normal">
                      Please explain how you want your products <br>
                      packaged. e.g. Do you want them shipped inside <br>
                      a custom gift box with your own art work? Or do <br>
                      you want them shipped in bulk using factory’s <br>
                      most common packaging?
                    </div>
                  </div>
                </span>
                <div class="lg:ml-2">(Max 200 characters)</div>
            </div>
            <ValidationProvider v-slot="{ errors }" rules="required|max:200">
                <textarea
                  class="border-2 rounded-lg p-3 w-full lg:h-32 h-48 text-lg"
                  name="The Packaging details"
                  placeholder="e.g. 100 pieces inside a custom gift box, 20 boxes per master carton."
                  v-model="formData.product_packaging_details"
                  @focus="isEnteringPackingDetails = true"
                  @blur="isEnteringPackingDetails = false"
                />
              <div class="input-error --end">{{ errors[0] ? checkValidation(errors[0],'The Packaging details') : ''}}</div>
            </ValidationProvider>
          </div>
          <div class="lg:pl-5 w-full lg:w-1/2">
            <div class="lg:pb-1 font-semibold text-lg lg:text-xl">
              Safety Standards<span class="text-red-600">*</span>
              <span class="md:relative" 
                  @mouseenter="shownTooltip = 'safetyStandards'"
                  @mouseleave="shownTooltip = null"
               >
                ⓘ
                <div v-if="shownTooltip === 'safetyStandards'" class="absolute z-20 px-3 small-tooltip">
                  <div class="p-2 pr-8 w-content text-xs font-normal border rounded-md bg-gray-b shadow-md">
                    e.g. FDA, CPSIA, REACH, UL, etc. 
                  </div>
                </div>
              </span>

            </div>
            <ValidationProvider v-slot="{ errors }" rules="required">
              <input
                name="firstName"
                class="form-input"
                placeholder="Type Safety Standards Here..."
                v-model="formData.product_safety_standards"
              />
              <div class="input-error --end">
                <span v-if="errors[0]">This field is required</span>
              </div>
            </ValidationProvider>
          </div>
          
        </div>
        <div class="border-b border-dark-b pt-6 text-2xl font-semibold relative leading-6 lg:leading-normal">
          Additional Product Information
           <span class="md:relative pl-1"
              @mouseenter="shownTooltip = 'additionalInfo'"
              @mouseleave="shownTooltip = null"
            >
              ⓘ
              <div
                v-if="shownTooltip === 'additionalInfo'"
                class="absolute z-20 px-3 medium-tooltip"
              >
                <div class="p-2 w-content text-xs font-normal border rounded-lg bg-gray-b leading-normal">
                  Please help to provide additional product information <br>
                  (reference images and/or reference links) to ensure <br>
                  clear understanding of your product sourcing request.
                </div>
              </div>
            </span>
        </div>
        <div class="">
          <br>
          The following information is recommended as it is helpful to explain your project goals.
        </div>
        <div class="flex flex-wrap w-full lg:pt-12 pt-7">
          <div class="lg:pb-0 pb-8 lg:pr-5 w-full lg:w-1/2">
            <div class="pb-1 font-semibold text-lg lg:text-xl relative">
              Product Images
              <span class="md:relative"
                @mouseenter="shownTooltip = 'image'"
                @mouseleave="shownTooltip = null"
              >
                ⓘ
                <div v-if="shownTooltip==='image'" class="absolute z-20 px-3 medium-tooltip">
                  <div class="p-2 w-content text-xs font-normal border rounded-lg bg-gray-b">
                    Please upload reference images of the <br> products you are looking for.
                  </div>
                </div>
              </span>
              <span class="text-base font-normal">(Max 4)</span>
            </div>
            <div class="flex flex-nowrap items-center">
              <div class="relative h-8 flex items-center">
                <input
                  type="file"
                  class="absolute inset-0 opacity-0 z-10 w-full h-12 overflow-hidden cursor-pointer"
                  ref="productImages"
                  accept="image/*,.pdf"
                  multiple
                  :disabled="(formData.product_images.length >= 4) || isAddingProductImage"
                  @change="addToProductImagesArray"
                >
                <button
                  class="w-38 lg:w-40 h-12 rounded-lg bg-dark-blue text-white text-lg mt-1 lg:text-xl font-semibold cursor-pointer"
                  type="button" 
                  :class="{'opacity-50': (formData.product_images.length >= 4) || isAddingProductImage}"
                  disabled
                >Browse Images</button>
              </div>
              <div
                class="pl-3 leading-5 text-xs sm:text-lg opacity-50"
              >
                (Pdf, Jpeg or Png format only.<br/> Max size 5MB)
              </div>
            </div>
            <div 
              v-if="formData.product_images.length && formData.product_images" 
              class="grid grid-cols-2 gap-2 lg:pt-3 lg:pb-3 pt-6">
              <div
                v-for="(image, index) in formData.product_images"
                :key="`image-${index}`"
                class="relative text-white text-xl p-1 product-image-parent"
              >
                <img class="product-image" v-if="typeof(image) === 'string'" :src="getImageType(image)">
                <img class="product-image" v-else :src="loadImage(image)">
                <span
                  class="cursor-pointer absolute right-0 top-0 m-2 leading-4 text-black"
                  @click="removeImage(index)"
                >x</span>
              </div>
            </div>
          </div> 
          <div class="lg:pb-0 pb-3 lg:pl-5 w-full lg:w-1/2">
            <div class="lg:pb-1 font-semibold text-lg lg:text-xl relative">
              Product Links
              <span class="md:relative"
                @mouseenter="shownTooltip = 'link'"
                @mouseleave="shownTooltip = null"
              >
                ⓘ
                <div v-if="shownTooltip==='link'" class="absolute z-20 px-3 large-tooltip">
                  <div class="p-2 w-content text-xs font-normal border rounded-lg bg-gray-b">
                    If available, add a link of the same or similar <br> 
                    product from site like Amazon. Having a <br>
                    reference like this helps your freelancer find <br> 
                    similar products from factories more easily.
                  </div>
                </div>
              </span>
              <span class="text-base font-normal">(Max 4)</span>
            </div>
            <div class="flex flex-nowrap items-center justify-between">
              <input
                name="productLink"
                placeholder="e.g. https://www.example.com"
                class="form-add-input text-xs sm:text-base"
                v-model="productLink"
                @input="productLinkError = false"
              />
              <button
                class="w-32 lg:w-38 h-12 lg:ml-4 ml-2 py-1 rounded-lg bg-dark-blue text-white text-lg lg:text-xl font-semibold lg:font-bold"
                type="button"
                :disabled="formData.product_links.length >= 4"
                :class="{'opacity-50': formData.product_links.length >= 4}"
                @click="addProductLink"
              >Add Link</button>
            </div>
            <div v-if="productLinkError" class="input-error pt-2 --end">Product Link must be a valid URL</div>
            <div v-if="formData.product_links.length && formData.product_links" class="pt-5 lg:pb-0 pb-2">
              <div
                v-for="(link, index) in formData.product_links"
                :key="`link-${index}`"
                class="flex justify-between items-center px-2 pt-1 rounded-lg"
              >
                <a :href="link" target="_blank" class="text-blue underline mr-3 truncate">{{ link }}</a>
                <span class="cursor-pointer text-lg" @click="removeProductLink(index)">x</span>
              </div>
            </div>
            <div class="input-error --end md:mb-0">
              <span v-if="imageAndLinkError">Please enter atleast 1 Product Image OR Product Link</span>
            </div>
          </div>
        </div>
        <div class="flex flex-wrap w-full">
          <div class="lg:pb-0 pb-3 lg:pr-5 w-full lg:w-1/2">
            <div class="lg:pb-1 font-semibold text-lg lg:text-xl">
              Target Price (USD)
            </div>
            <ValidationProvider v-slot="{ errors }" rules="number|decimal">
              <input
                name="The Target Price"
                class="form-input"
                placeholder="Type Target Price Here..."
                v-model="formData.product_target_price"
              />
              <div class="input-error --end">{{ errors[0] ? checkValidation(errors[0],'The Target Price') : ''}}</div>
            </ValidationProvider>
          </div>
          <div class="lg:pl-5 w-full lg:w-1/2">
            <div class="lg:pb-1 lg:flex items-center relative">
              <span class="font-semibold text-lg lg:text-xl leading-6 lg:leading-normal">
                Decoration Specifications
              </span>
               <span class="md:relative px-1 font-semibold text-lg"
                @mouseenter="shownTooltip = 'specifications'"
                @mouseleave="shownTooltip = null"
              >
                ⓘ
                <div
                  v-if="shownTooltip==='specifications'"
                  class="absolute z-20 px-3 large-tooltip"
                >
                  <div class="p-2 w-content text-xs font-normal border rounded-lg bg-gray-b leading-normal">
                   Please explain if you want your products <br>
                   decorated with your logo and if so please <br> 
                   describe the artwork <br> 
                   (e.g. 3 color logo printed on the product)
                  </div>
                </div>
               </span>
               <div class="lg:ml-2">(Max 200 characters)</div>
            </div>
            <ValidationProvider v-slot="{ errors }" rules="max:200">
              <textarea
                  class="border-2 rounded-lg p-3 w-full lg:h-32 h-48 text-lg"
                  name="The Decoration specifications"
                  placeholder="e.g. 3 color logo printed on one side"
                  v-model="formData.product_decoration_details"
                />
              <div class="input-error --end">{{ errors[0] ? checkValidation(errors[0],'The Decoration specifications') : ''}}</div>
            </ValidationProvider>
          </div>
        </div>
        <div class="flex justify-between md:justify-end pt-5 lg:pt-12 lg:pb-10 pb-8">
          <button 
             class="text-blue border-2 border-blue rounded-full py-3 px-3 md:px-8 text-lg lg:text-xl font-bold mr-3"
            :class="{'opacity-50' : isSavingData}"
            @click="PreviousTab" 
          >
            Back
          </button>
          &nbsp;
          &nbsp;
          <br/>
          <button 
            class="bg-blue text-white rounded-full py-3 px-3 md:px-8 text-lg lg:text-xl font-bold"
            :disabled="isSavingData"
            :class="{'opacity-50' : isSavingData}"
            @click="validateFields" 
          >
            Save & Continue
          </button>
        </div>
      </div>
    </form>
    <div class="text-center not-ready-section pb-3 pt-6">
        Not ready to commit? Questions? Let’s talk <br class="md:hidden"/> about your needs!
        <br/>
    </div>
    <div class="flex justify-center items-center">
      <button @click="getBookACallUrl" class="bg-theme  text-white rounded-full py-2 px-5 text-lg lg:text-xl font-semibold">
        Book a Call
      </button>
    </div>
  </ValidationObserver>
</template>
<script>
import { mapGetters , mapActions  } from 'vuex'
export default {
  name: "ProductDetailsForm",
  props: {
    userData: {
      type: Object,
      default: null
    },
  },
  created() {
    this.formData = { ...this.formData, ...this.userData }
    this.setDraftData()
  },
  data() {
    return {
      formData: {
        product_category_id: '',
        product_sub_category_id: '',
        product_shipping_terms: 'FOB',
        product_images: [],
        product_links: [],
        imageTokens: [],
      },
      productLink: '',
      shownTooltip: null,
      productLinkError: false,
      imageAndLinkError: false,
      isEnteringPackingDetails: false,
      isAddingProductImage: false,
      isSavingData: false,
      productImages: [],
     
    };
  },
  mounted() {
    this.fetchFreelancerCategories()
    if (!this.categories || !this.categories.length) this.fetchFreelancerCategories() 
  },
  computed: {
    ...mapGetters([
      'getFreelancerCategories'
    ]),
    isValidproductLink() {
      return !this.formData.product_links.some(link => (link === this.productLink))
    },
    categories() {
      return this.getFreelancerCategories.length ? this.getFreelancerCategories : []
    },
    selectedCategory() {
      return this.categories.find(cat => cat.category_id_token === this.formData.product_category_id)
    },
    subCategories() {
      return this.selectedCategory ? this.selectedCategory.sub_categories : []
    },
    selectedSubCategory() {
      return this.subCategories.find(sub => sub.sub_category_id === this.formData.product_sub_category_id)
    },
  },
  watch: {
    selectedCategory() {
      if (this.formData.product_sub_category_id && !this.selectedSubCategory) {
        this.formData.product_sub_category_id = ''
        this.$refs.subCategoriesField.reset()
      }
    }
  },
  methods: {
    ...mapActions([
      'fetchFreelancerCategories',
      'updateProductInformation',
      'addProductImage',
      'removeProductImage'
    ]),
    getBookACallUrl() {
      window.open(process.env.VUE_APP_BOOK_A_CALL_URL,'_blank');      
    },
    validateURL(str) {
      var pattern = new RegExp('[-a-zA-Z0-9@:%._\\+~#=]{1,256}\\.[a-zA-Z0-9()]{1,6}\\b([-a-zA-Z0-9()@%_\\+.~#?&//=]*)'); // fragment locator
        return pattern.test(str);
    },
    PreviousTab() {
      this.$emit('changeTab')
    },
    setDraftData() {
      if (!this.formData.product_shipping_terms) this.formData.product_shipping_terms = 'FOB'
      if (this.formData.product_category) delete this.formData.product_category
      if (this.formData.product_sub_category) delete this.formData.product_sub_category
      if (this.userData && this.userData.product_images.length) {
        this.formData.product_images = []
        this.userData.product_images.forEach(img => {
          this.formData.product_images.push(img.image_url)
          this.formData.imageTokens.push(img.image_token)
        })
      }
    },
    addProductLink() {
      if (this.productLink.length && this.formData.product_links.length < 4) {
          const isValidUrl = this.validateURL(this.productLink)
        if(!isValidUrl) {
          this.productLinkError = true
          return
        }
        if (!this.isValidproductLink) return
        this.productLinkError = false
        this.formData.product_links.push(this.productLink)
        this.productLink = ''
        this.imageAndLinkError = false
      }
    },
    removeProductLink(index) {
      this.formData.product_links.splice(index, 1)
    },
    addToProductImagesArray(event) {
      if (event.target.files.length + this.formData.product_images.length <= 4) {
        this.productImages = []
        event.target.files.forEach((file) => {
          this.isAddingProductImage = true
          this.addToProductImage(file)
        })
        if (this.productImages.length) this.addImage()
      } else {
        this.$notify({
          group: 'buyHive',
          type: 'error',
          text: 'Only 4 Files Can be Uploaded'
        })
        this.$refs.productImages.value = ''
      }
    },
    addToProductImage(file) {
      if (file && !(file.size/(1024*1024) <= 5)) {
        this.$notify({
          group: 'buyHive',
          type: 'error',
          text: `${file.name} Product Image must be under 5MB.`
        })
        this.isAddingProductImage = false
        return
      }
      if (file && (file.type.includes('image') || file.type == 'application/pdf')) {
        this.productImages.push(file)
      }
      else {
         this.$notify({
          group: 'buyHive',
          type: 'error',
          text: `${file.name} File format is not correct.`
        })
        this.isAddingProductImage = false
        return
      }
    },
    async addImage() {
      let imgFormData = new FormData()
      this.productImages.forEach(productImg => {
        imgFormData.append('product_images[]', productImg) 
      })
      const response = await this.addProductImage(imgFormData)
      if (!response.result) {
        this.$notify({
          group: 'buyHive',
          type: 'error',
          text: response.message
        })
        this.isAddingProductImage = false
        return
      }
      response.tokens.forEach(token => {
        this.formData.imageTokens.push(token)
      })
      this.productImages.forEach(img => {
        this.formData.product_images.push(img)
      })
      this.$refs.productImages.value = ''
      this.imageAndLinkError = false
      this.isAddingProductImage = false
    },
    async removeImage(index) {
      await this.removeProductImage(this.formData.imageTokens[index])
      this.formData.product_images.splice(index, 1)
      this.formData.imageTokens.splice(index, 1)
    },
    loadImage(image) {
      if(image.type == 'application/pdf') {
        return process.env.VUE_APP_PDF_PNG_IMAGE
      }
      return (window.URL || window.webkitURL).createObjectURL(image)
    },
    getImageType(filename) {
      let extension = filename.substring(filename.lastIndexOf('.')+1, filename.length) || filename;
      if(extension == 'pdf') {
        return process.env.VUE_APP_PDF_PNG_IMAGE
      } 
      return filename
    },
    validateFields(){
      // if (!this.formData.product_links.length && !this.formData.product_images.length) {
      //   this.imageAndLinkError = true
      // } else {
      //   this.imageAndLinkError = false
      // }
    },
    async onSubmit() {
      this.isSavingData = true
      this.addProductLink()
      this.$emit('updateSourcingForm', { productData: this.formData })
      let productFormData = {...this.formData}
      delete productFormData.product_images
      delete productFormData.imageTokens
      let response = await this.updateProductInformation({ ...productFormData, _method: 'PUT' })
      if (response.result) this.$emit('proceedNext')
      this.isSavingData = false
    },
    checkValidation(message, field) {
      return message === `${field} is not valid.` ? 'This field is Required' : message
    }
  },
};
</script>
<style scoped lang="scss">
.not-ready-section {
  @screen md {
  font-size: 26px;
  line-height: 39px;
  font-weight: 700;
  }
  font-size: 24px;
  line-height: 28px;
  font-weight: 600;
}
  .image-input {
    max-width: calc(100% - 5.25rem);
  }
  .small-tooltip {
    left: 0;
    top: -2.5rem; 
    @screen lg {
      left: 2rem;
      top: -0.5rem;
    }
  }
  .product-image {
    max-width: 90%;
  }
  .product-image-parent img { 
    width: -webkit-fill-available;
  }
  .medium-tooltip {
    left: 0;
    top: -3.5rem;
    @screen lg {
      left: 2rem;
      top: -1.5rem;
    }
  }
  .large-tooltip {
    left: 0;
    top: -5.5rem; 
    @screen lg {
      left: 2rem;
      top: -2.5rem;
    }
  }
</style>
